import { useEffect, useState, useCallback } from "react";
import {
  AlertCircle, Clock, Filter, RefreshCw, Search, X,
  MessageSquareX, Swords, Flame, Hash, Angry, CalendarDays, UserCheck
} from "lucide-react";
import { dashboardService, type Alert } from "@/services/dashboardService";
import { alertService } from "@/services/alertService";
import { AlertDetailModal } from "./AlertDetailModal";
import { useApp } from "@/App";
import { useTeacherTheme } from "@/contexts/TeacherThemeContext";
import { safeLog } from '@/utils/safeLog';

// Alert type icon — matches TeacherHome
function AlertTypeIcon({ type, className }: { type: string; className?: string }) {
  const t = type?.toLowerCase().replace(/[\s_]/g, '');
  if (t?.includes('cyberbull') || t?.includes('bullying')) return <MessageSquareX className={className} />;
  if (t?.includes('selfharm') || t?.includes('suicide') || t?.includes('harm')) return <Flame className={className} />;
  if (t?.includes('violence') || t?.includes('threat')) return <Swords className={className} />;
  if (t?.includes('hate')) return <Angry className={className} />;
  if (t?.includes('profan')) return <Hash className={className} />;
  return <AlertCircle className={className} />;
}

const ALERT_TYPES = [
  { value: 'all', label: 'All Types' },
  { value: 'cyberbullying', label: 'Cyberbullying' },
  { value: 'self_harm', label: 'Self Harm' },
  { value: 'violence', label: 'Violence' },
  { value: 'threat', label: 'Threat' },
  { value: 'profanity', label: 'Profanity' },
  { value: 'hate_speech', label: 'Hate Speech' },
];

const DATE_RANGES = [
  { value: 'all', label: 'All Time' },
  { value: 'today', label: 'Today' },
  { value: '7d', label: 'Last 7 days' },
];

export function ActiveAlerts() {
  const { user } = useApp();
  const { dm } = useTeacherTheme();
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedAlertId, setSelectedAlertId] = useState<string | null>(null);
  const [filterSeverity, setFilterSeverity] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterDate, setFilterDate] = useState<string>('all');
  const [myAlerts, setMyAlerts] = useState(false);
  const [search, setSearch] = useState('');
  const [quickActionLoading, setQuickActionLoading] = useState<string | null>(null);
  const [assignLoading, setAssignLoading] = useState<string | null>(null);

  const fetchAlerts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await dashboardService.getTeacherAlerts(page, 10);
      if (page === 1) {
        setAlerts(response.data);
      } else {
        setAlerts(prev => [...prev, ...response.data]);
      }
      setTotalPages(response.totalPages);
    } catch (error) {
      safeLog.error("Failed to load alerts", error);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 30000);
    return () => clearInterval(interval);
  }, [fetchAlerts]);

  // Open a specific alert when navigated from student profile
  useEffect(() => {
    const handler = (e: Event) => {
      const alertId = (e as CustomEvent).detail?.alertId;
      if (alertId) setSelectedAlertId(alertId);
    };
    window.addEventListener('teacher-open-alert', handler);
    return () => window.removeEventListener('teacher-open-alert', handler);
  }, []);

  useEffect(() => {
    setPage(p => Math.min(p, Math.max(1, totalPages)));
  }, [totalPages]);

  const handleQuickAcknowledge = async (alertId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      setQuickActionLoading(alertId);
      await alertService.acknowledgeAlert(alertId);
      await fetchAlerts();
    } catch (error) {
      safeLog.error("Failed to acknowledge alert", error);
    } finally {
      setQuickActionLoading(null);
    }
  };

  const handleAssignToSelf = async (alertId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      setAssignLoading(alertId);
      await alertService.assignToSelf(alertId);
      await fetchAlerts();
    } catch (error) {
      safeLog.error("Failed to assign alert", error);
    } finally {
      setAssignLoading(null);
    }
  };

  const formatTimeAgo = (dateString: string) => {
    try {
      const seconds = Math.floor((Date.now() - new Date(dateString).getTime()) / 1000);
      if (seconds < 60) return 'Just now';
      const minutes = Math.floor(seconds / 60);
      if (minutes < 60) return `${minutes}m ago`;
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `${hours}h ago`;
      return `${Math.floor(hours / 24)}d ago`;
    } catch { return 'Unknown'; }
  };

  // ── Client-side filtering ──────────────────────────────────────────────────
  const filteredAlerts = alerts.filter(a => {
    if (myAlerts && a.assignedTo?.id !== user?.id) return false;
    if (filterSeverity !== 'all' && a.severity !== filterSeverity) return false;
    if (filterType !== 'all') {
      const t = a.alertType?.toLowerCase().replace(/[\s_]/g, '');
      const ft = filterType.toLowerCase().replace(/[\s_]/g, '');
      if (!t?.includes(ft)) return false;
    }
    if (filterDate === 'today') {
      const start = new Date(); start.setHours(0, 0, 0, 0);
      if (new Date(a.createdAt) < start) return false;
    } else if (filterDate === '7d') {
      const start = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
      if (new Date(a.createdAt) < start) return false;
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      if (!a.studentName?.toLowerCase().includes(q) && !a.alertType?.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const severityIconBg = (severity: string) => {
    switch (severity) {
      case 'critical': case 'high': return 'bg-red-100 text-red-600';
      case 'medium': return 'bg-orange-100 text-orange-500';
      default: return 'bg-yellow-100 text-yellow-600';
    }
  };

  const severityBadge = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-600 text-white';
      case 'high': return 'bg-red-500 text-white';
      case 'medium': return 'bg-amber-500 text-white';
      default: return 'bg-emerald-500 text-white';
    }
  };

  const statusBadge = (status: string) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-700';
      case 'acknowledged': return 'bg-purple-100 text-purple-700';
      case 'investigating': return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const hasActiveFilters = filterSeverity !== 'all' || filterType !== 'all' || filterDate !== 'all' || search.trim() !== '' || myAlerts;

  const clearFilters = () => {
    setFilterSeverity('all');
    setFilterType('all');
    setFilterDate('all');
    setSearch('');
    setMyAlerts(false);
  };

  return (
    <div className="max-w-7xl mx-auto space-y-4">

      {/* Toolbar */}
      <div className={`border rounded-xl p-4 space-y-3 shadow-sm ${dm ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>

        {/* Top row: count + refresh */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold border ${dm ? 'bg-slate-700 text-slate-200 border-slate-600' : 'bg-slate-100 text-slate-700 border-slate-200'}`}>
              <AlertCircle className="h-3.5 w-3.5 text-red-500" />
              {filteredAlerts.length} alert{filteredAlerts.length !== 1 ? 's' : ''}
            </span>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 text-xs text-slate-500 hover:text-red-500 transition"
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              >
                <X className="h-3 w-3" /> Clear filters
              </button>
            )}
          </div>
          <button onClick={fetchAlerts} disabled={loading} className="btn-glass btn-glass-pill flex items-center gap-1.5">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${dm ? 'text-slate-500' : 'text-slate-400'}`} />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by student name or alert type..."
            className={`w-full pl-9 pr-4 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${dm ? 'bg-slate-700 border-slate-600 text-slate-200 placeholder-slate-500' : 'bg-white border-slate-200 text-slate-800'}`}
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Filter pills */}
        <div className="flex flex-wrap items-center gap-2" data-dm={dm}>
          <Filter className="h-3.5 w-3.5 text-slate-400 shrink-0" />

          {/* Severity */}
          <div className="flex items-center gap-1">
            {['all', 'critical', 'high', 'medium', 'low'].map(sev => (
              <button
                key={sev}
                onClick={() => setFilterSeverity(sev)}
                className={`btn-glass btn-glass-pill ${filterSeverity === sev ? 'btn-glass-blue' : ''}`}
              >
                {sev === 'all' ? 'All Severity' : sev.charAt(0).toUpperCase() + sev.slice(1)}
              </button>
            ))}
          </div>

          <div className={`w-px h-4 ${dm ? 'bg-slate-600' : 'bg-slate-200'}`} />

          {/* Alert type */}
          <div className="flex items-center gap-1 flex-wrap">
            {ALERT_TYPES.map(({ value, label }) => (
              <button
                key={value}
                onClick={() => setFilterType(value)}
                className={`btn-glass btn-glass-pill ${filterType === value ? 'btn-glass-violet' : ''}`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className={`w-px h-4 ${dm ? 'bg-slate-600' : 'bg-slate-200'}`} />

          {/* Date range */}
          <div className="flex items-center gap-1">
            <CalendarDays className="h-3.5 w-3.5 text-slate-400" />
            {DATE_RANGES.map(({ value, label }) => (
              <button
                key={value}
                onClick={() => setFilterDate(value)}
                className={`btn-glass btn-glass-pill ${filterDate === value ? 'btn-glass-emerald' : ''}`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className={`w-px h-4 ${dm ? 'bg-slate-600' : 'bg-slate-200'}`} />

          {/* My Alerts toggle */}
          <button
            onClick={() => setMyAlerts(v => !v)}
            className={`btn-glass btn-glass-pill flex items-center gap-1.5 ${myAlerts ? 'btn-glass-blue' : ''}`}
          >
            <UserCheck className="h-3.5 w-3.5" />
            My Alerts
          </button>
        </div>
      </div>

      {/* Alerts List */}
      <div className="space-y-3">
        {loading && alerts.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
            <RefreshCw className="animate-spin h-8 w-8 text-blue-500 mx-auto mb-3" />
            <p className="text-slate-500 text-sm">Loading alerts...</p>
          </div>
        ) : filteredAlerts.length === 0 ? (
          <div className={`text-center py-16 rounded-xl border shadow-sm ${dm ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
            <AlertCircle className="h-10 w-10 text-emerald-400 mx-auto mb-3" />
            <p className={`font-medium ${dm ? 'text-slate-300' : 'text-slate-600'}`}>No alerts found</p>
            <p className={`text-sm mt-1 ${dm ? 'text-slate-500' : 'text-slate-400'}`}>
              {hasActiveFilters ? 'Try adjusting your filters.' : 'All clear! No active alerts.'}
            </p>
          </div>
        ) : (
          filteredAlerts.map((alert) => (
            <div
              key={alert.id}
              className={`border rounded-xl shadow-sm hover:shadow-md transition cursor-pointer ${dm ? 'bg-slate-800 border-slate-700 hover:border-slate-600' : 'bg-white border-slate-200'}`}
              onClick={() => setSelectedAlertId(alert.id)}
            >
              <div className="flex gap-4 p-4">
                {/* Type icon */}
                <div className={`flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full ${severityIconBg(alert.severity)}`}>
                  <AlertTypeIcon type={alert.alertType} className="h-5 w-5" />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  {/* Header row */}
                  <div className="flex items-start justify-between gap-3 mb-1">
                    <div className="min-w-0">
                      <h3 className={`font-semibold truncate ${dm ? 'text-slate-100' : 'text-slate-900'}`}>{alert.studentName}</h3>
                      <p className={`text-xs capitalize truncate ${dm ? 'text-slate-400' : 'text-slate-500'}`}>{alert.alertType?.replace(/_/g, ' ')}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${severityBadge(alert.severity)} capitalize`}>
                        {alert.severity}
                      </span>
                      {alert.status && (
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge(alert.status)} capitalize`}>
                          {alert.status}
                        </span>
                      )}
                    </div>
                  </div>

                  {alert.description && (
                    <p className={`text-sm mb-3 line-clamp-2 ${dm ? 'text-slate-400' : 'text-slate-600'}`}>{alert.description}</p>
                  )}

                  {/* Footer row: time (prominent) + actions */}
                  <div className="flex items-center justify-between">
                    {/* Time — front and center */}
                    <div className="flex items-center gap-1.5">
                      <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
                        (() => {
                          const mins = (Date.now() - new Date(alert.createdAt).getTime()) / 60000;
                          if (mins < 30) return 'bg-red-100 text-red-700';
                          if (mins < 120) return 'bg-orange-100 text-orange-700';
                          return 'bg-slate-100 text-slate-500';
                        })()
                      }`}>
                        <Clock className="h-3 w-3" />
                        {formatTimeAgo(alert.createdAt)}
                      </div>
                    </div>
                    {/* Actions */}
                    <div className="flex gap-1.5">
                      {alert.status === 'new' && (
                        <button
                          onClick={(e) => handleQuickAcknowledge(alert.id, e)}
                          disabled={quickActionLoading === alert.id}
                          className="btn-glass btn-glass-pill btn-glass-purple disabled:opacity-50"
                        >
                          {quickActionLoading === alert.id ? 'Acknowledging…' : 'Acknowledge'}
                        </button>
                      )}
                      <button
                        onClick={(e) => handleAssignToSelf(alert.id, e)}
                        disabled={assignLoading === alert.id}
                        className="btn-glass btn-glass-pill flex items-center gap-1 disabled:opacity-50"
                      >
                        <UserCheck className="h-3 w-3" />
                        {assignLoading === alert.id ? 'Assigning…' : 'Assign to me'}
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setSelectedAlertId(alert.id); }}
                        className="btn-glass btn-glass-pill"
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Load More */}
      {page < totalPages && (
        <div className="flex justify-center">
          <button
            onClick={() => setPage(p => p + 1)}
            disabled={loading}
            className="btn-glass btn-glass-pill flex items-center gap-2"
          >
            {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Load More
          </button>
        </div>
      )}

      {/* Alert Detail Modal */}
      {selectedAlertId && (
        <AlertDetailModal
          alertId={selectedAlertId}
          isOpen={!!selectedAlertId}
          onClose={() => setSelectedAlertId(null)}
          onStatusChange={fetchAlerts}
        />
      )}
    </div>
  );
}
